import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  GlobalSignOutCommand,
  GetUserCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  type InitiateAuthCommandOutput,
  type AuthenticationResultType,
} from '@aws-sdk/client-cognito-identity-provider';
import { tokenManager } from './token-manager';
import {
  type AuthResult,
  type AuthResponse,
  type AuthUser,
  type AuthTokens,
  COGNITO_ERROR_MESSAGES,
  type CognitoErrorCode,
} from './types';

const AWS_REGION = import.meta.env.VITE_AWS_REGION || 'us-east-1';
const USER_POOL_CLIENT_ID = import.meta.env.VITE_AWS_USER_POOL_CLIENT_ID || '';

/**
 * Generate correlation ID for request tracing.
 */
function generateCorrelationId(): string {
  return `auth-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Structured logger for auth operations.
 * All auth events logged with correlation ID for debugging.
 */
const authLogger = {
  info: (correlationId: string, event: string, details?: Record<string, unknown>) => {
    console.log(`[Auth] ${correlationId} ${event}`, details ?? '');
  },
  error: (
    correlationId: string,
    event: string,
    errorCode: string,
    message: string
  ) => {
    console.error(`[Auth] ${correlationId} ${event}`, { errorCode, message });
  },
};

/**
 * Map Cognito error to user-friendly message.
 */
function mapCognitoError(error: unknown): { code: string; message: string } {
  if (error && typeof error === 'object' && 'name' in error) {
    const code = (error as { name: string }).name;
    const message =
      COGNITO_ERROR_MESSAGES[code as CognitoErrorCode] ??
      'An unexpected error occurred';
    return { code, message };
  }
  return { code: 'UnknownError', message: 'An unexpected error occurred' };
}

/**
 * Parse authentication result from Cognito response.
 */
function parseAuthResult(
  authResult: AuthenticationResultType,
  correlationId: string
): AuthResult {
  const { AccessToken, IdToken, RefreshToken, ExpiresIn } = authResult;

  if (!AccessToken || !IdToken || !RefreshToken) {
    throw new Error('Incomplete authentication result from Cognito');
  }

  // Calculate expiry time
  const expiresAt = Date.now() + (ExpiresIn ?? 3600) * 1000;

  const tokens: AuthTokens = {
    accessToken: AccessToken,
    idToken: IdToken,
    refreshToken: RefreshToken,
    expiresAt,
  };

  // Decode ID token to get user info
  const payload = tokenManager.decodeToken(IdToken);
  if (!payload) {
    throw new Error('Failed to decode ID token');
  }

  const user: AuthUser = {
    id: (payload['sub'] as string) ?? '',
    email: (payload['email'] as string) ?? '',
    tenantId: (payload['custom:tenantId'] as string) ?? '',
    role: payload['custom:role'] as string | undefined,
    departmentId: payload['custom:departmentId'] as string | undefined,
    name: payload['name'] as string | undefined,
    emailVerified: (payload['email_verified'] as boolean) ?? false,
  };

  authLogger.info(correlationId, 'auth.parsed', {
    userId: user.id,
    tenantId: user.tenantId,
    expiresAt: new Date(expiresAt).toISOString(),
  });

  return { user, tokens };
}

/**
 * Cognito authentication service.
 * Uses Direct AWS SDK for full visibility and control.
 */
export class CognitoAuthService {
  private client: CognitoIdentityProviderClient;
  private clientId: string;

  constructor() {
    this.client = new CognitoIdentityProviderClient({
      region: AWS_REGION,
    });
    this.clientId = USER_POOL_CLIENT_ID;
  }

  /**
   * Sign in with email and password.
   * Returns either a successful auth result or a challenge (MFA, new password, etc.)
   */
  async signIn(email: string, password: string): Promise<AuthResponse> {
    const correlationId = generateCorrelationId();
    authLogger.info(correlationId, 'signIn.start', { email });

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: this.clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const response: InitiateAuthCommandOutput = await this.client.send(command);

      // Check for challenges (MFA, new password required, etc.)
      if (response.ChallengeName) {
        authLogger.info(correlationId, 'signIn.challenge', {
          challengeName: response.ChallengeName,
        });

        return {
          type: 'challenge',
          data: {
            challengeName: response.ChallengeName as 'NEW_PASSWORD_REQUIRED' | 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA',
            session: response.Session ?? '',
            challengeParameters: response.ChallengeParameters,
          },
        };
      }

      // Successful authentication
      if (!response.AuthenticationResult) {
        throw new Error('No authentication result returned');
      }

      const result = parseAuthResult(response.AuthenticationResult, correlationId);

      // Store tokens
      tokenManager.setTokens(result.tokens);

      authLogger.info(correlationId, 'signIn.success', {
        userId: result.user.id,
      });

      return { type: 'success', data: result };
    } catch (error) {
      const { code, message } = mapCognitoError(error);
      authLogger.error(correlationId, 'signIn.failed', code, message);
      throw new AuthError(code, message);
    }
  }

  /**
   * Respond to authentication challenge (MFA, new password, etc.)
   */
  async respondToChallenge(
    challengeName: string,
    session: string,
    responses: Record<string, string>
  ): Promise<AuthResponse> {
    const correlationId = generateCorrelationId();
    authLogger.info(correlationId, 'respondToChallenge.start', { challengeName });

    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: this.clientId,
        ChallengeName: challengeName as 'NEW_PASSWORD_REQUIRED' | 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA',
        Session: session,
        ChallengeResponses: responses,
      });

      const response = await this.client.send(command);

      if (response.ChallengeName) {
        return {
          type: 'challenge',
          data: {
            challengeName: response.ChallengeName as 'NEW_PASSWORD_REQUIRED' | 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA',
            session: response.Session ?? '',
            challengeParameters: response.ChallengeParameters,
          },
        };
      }

      if (!response.AuthenticationResult) {
        throw new Error('No authentication result returned');
      }

      const result = parseAuthResult(response.AuthenticationResult, correlationId);
      tokenManager.setTokens(result.tokens);

      authLogger.info(correlationId, 'respondToChallenge.success', {
        userId: result.user.id,
      });

      return { type: 'success', data: result };
    } catch (error) {
      const { code, message } = mapCognitoError(error);
      authLogger.error(correlationId, 'respondToChallenge.failed', code, message);
      throw new AuthError(code, message);
    }
  }

  /**
   * Refresh tokens using the refresh token.
   */
  async refreshTokens(): Promise<AuthResult> {
    const correlationId = generateCorrelationId();
    const refreshToken = tokenManager.getRefreshToken();

    if (!refreshToken) {
      authLogger.error(correlationId, 'refresh.failed', 'NoRefreshToken', 'No refresh token available');
      throw new AuthError('NoRefreshToken', 'No refresh token available');
    }

    authLogger.info(correlationId, 'refresh.start');

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: this.clientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      });

      const response = await this.client.send(command);

      if (!response.AuthenticationResult) {
        throw new Error('No authentication result returned');
      }

      // Refresh doesn't return a new refresh token, keep the old one
      const result = parseAuthResult(
        {
          ...response.AuthenticationResult,
          RefreshToken: refreshToken,
        },
        correlationId
      );

      tokenManager.setTokens(result.tokens);

      authLogger.info(correlationId, 'refresh.success', {
        userId: result.user.id,
      });

      return result;
    } catch (error) {
      const { code, message } = mapCognitoError(error);
      authLogger.error(correlationId, 'refresh.failed', code, message);

      // Clear tokens on refresh failure
      tokenManager.clearTokens();

      throw new AuthError(code, message);
    }
  }

  /**
   * Sign out globally (invalidates all sessions).
   */
  async signOut(): Promise<void> {
    const correlationId = generateCorrelationId();
    authLogger.info(correlationId, 'signOut.start');

    const accessToken = tokenManager.getAccessToken();

    try {
      if (accessToken) {
        const command = new GlobalSignOutCommand({
          AccessToken: accessToken,
        });
        await this.client.send(command);
      }

      tokenManager.clearTokens();
      authLogger.info(correlationId, 'signOut.success');
    } catch (error) {
      // Still clear local tokens even if global signout fails
      tokenManager.clearTokens();

      const { code, message } = mapCognitoError(error);
      authLogger.error(correlationId, 'signOut.partial', code, message);
      // Don't throw - we've cleared local tokens, which is the important part
    }
  }

  /**
   * Get current user from access token.
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    const correlationId = generateCorrelationId();
    const accessToken = tokenManager.getAccessToken();

    if (!accessToken) {
      return null;
    }

    try {
      const command = new GetUserCommand({
        AccessToken: accessToken,
      });

      const response = await this.client.send(command);

      // Extract attributes into a map
      const attributes: Record<string, string> = {};
      response.UserAttributes?.forEach((attr) => {
        if (attr.Name && attr.Value) {
          attributes[attr.Name] = attr.Value;
        }
      });

      const user: AuthUser = {
        id: attributes['sub'] ?? '',
        email: attributes['email'] ?? '',
        tenantId: attributes['custom:tenantId'] ?? '',
        role: attributes['custom:role'],
        name: attributes['name'],
        emailVerified: attributes['email_verified'] === 'true',
      };

      authLogger.info(correlationId, 'getCurrentUser.success', {
        userId: user.id,
      });

      return user;
    } catch (error) {
      const { code, message } = mapCognitoError(error);
      authLogger.error(correlationId, 'getCurrentUser.failed', code, message);
      return null;
    }
  }

  /**
   * Initiate forgot password flow. Sends a verification code to the user's email.
   */
  async forgotPassword(email: string): Promise<void> {
    const correlationId = generateCorrelationId();
    authLogger.info(correlationId, 'forgotPassword.start', { email });

    try {
      const command = new ForgotPasswordCommand({
        ClientId: this.clientId,
        Username: email,
      });

      await this.client.send(command);
      authLogger.info(correlationId, 'forgotPassword.codeSent', { email });
    } catch (error) {
      const { code, message } = mapCognitoError(error);
      authLogger.error(correlationId, 'forgotPassword.failed', code, message);
      throw new AuthError(code, message);
    }
  }

  /**
   * Confirm forgot password with verification code and new password.
   */
  async confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    const correlationId = generateCorrelationId();
    authLogger.info(correlationId, 'confirmForgotPassword.start', { email });

    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: this.clientId,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword,
      });

      await this.client.send(command);
      authLogger.info(correlationId, 'confirmForgotPassword.success', { email });
    } catch (error) {
      const { code: errorCode, message } = mapCognitoError(error);
      authLogger.error(correlationId, 'confirmForgotPassword.failed', errorCode, message);
      throw new AuthError(errorCode, message);
    }
  }

  /**
   * Check if user is authenticated (has valid tokens).
   */
  isAuthenticated(): boolean {
    return !tokenManager.isExpired();
  }
}

/**
 * Custom error class for auth errors.
 * Preserves the Cognito error code for debugging.
 */
export class AuthError extends Error {
  constructor(
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

// Export singleton instance
export const cognitoAuthService = new CognitoAuthService();
